import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BlockIcon from "@mui/icons-material/Block";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { resolveLocalized } from "@shared/i18n";
import { guestRequestIsRevenue } from "@shared/guestRequests";
import type {
  GuestRequest,
  GuestRequestStatus,
  GuestRequestTransition,
} from "@shared/types/guestRequest";
import { foyerApi } from "../../http/foyer";
import { useCan } from "../../auth/AuthContext";
import { tokens } from "../../theme";
import GuestRequestActionDialog from "./GuestRequestActionDialog";
import {
  PAYMENT_METHOD_LABEL,
  PAYMENT_STATUS_LABEL,
  REQUEST_STATUS_LABEL,
  SECONDARY_TRANSITIONS,
  TRANSITION_LABEL,
  WAIT_LATE_MS,
  WAIT_WARN_MS,
  availableTransitions,
  formatSlot,
  latestActor,
  latestReason,
  requestAmount,
  requestShape,
  selectionLine,
  selectionQuantity,
  waitedLabel,
} from "./guestRequestCopy";

/**
 * The staff inbox — the queue the hotel works its requests off (`POS-9`).
 *
 * ## It polls, and that is POS-6's decision, not an oversight
 *
 * `docs/room-offers.md` §13: *the room is pushed to, the CMS polls*. The
 * television gets `guest_request_changed` over its own room channel because a
 * guest is looking at one screen and needs it to answer; the queue polls for
 * exactly the reason `ServiceRequestQueue` does — a queue that only learns
 * about work through a socket is a queue that silently empties when the socket
 * drops, and an empty queue is indistinguishable from a quiet evening.
 *
 * ## Two clocks, deliberately
 *
 * The list refreshes every {@link POLL_MS}; the "waited" column ages every
 * second. They are separate because they answer different questions: one is
 * "is there new work", the other is "how long has this person been waiting",
 * and tying the second to the first would make a request sitting for twenty
 * minutes look no different from one that landed twenty seconds ago until the
 * next fetch happened to land. A queue whose clock is frozen until you reload
 * is a queue nobody trusts.
 *
 * ## What each row shows, and what it deliberately does not
 *
 * Three shapes, decided by {@link requestShape}:
 *
 * - an **order** carries quantity, amount, how it is paid and whether the
 *   payment has posted;
 * - a **booking** carries the hour it took and the party it seats;
 * - an **asap** booking — the "call for staff" flow — carries the room, the
 *   clock and whatever was picked, and nothing else, *because nothing else
 *   exists*. Drawing an empty slot cell for it would read as data that failed
 *   to load rather than as a request that never had any.
 *
 * A **zero-price request is legitimate** and is never drawn as a problem: the
 * amenity picker (towels, refills) is the third demo flow, and §7 is explicit
 * that an absent price means free rather than zero.
 *
 * ## The buttons come from the machine
 *
 * {@link availableTransitions} asks `@shared/guestRequests` which verbs the row
 * admits. Nothing here knows that `declined` is final or that `refund` is
 * reachable from two states — so a final row simply offers nothing, and a sixth
 * edge added to §13 would appear here without this file changing.
 */

/** How often the list refetches. Care's queue uses the same interval. */
const POLL_MS = 8000;
/** How often the waiting clock moves. One second, because that is what "ages
 *  visibly" means to somebody watching a demo. */
const TICK_MS = 1000;

/** The statuses that are still WORK. Everything else is history. */
const OPEN_STATUSES: readonly GuestRequestStatus[] = ["pending", "accepted"];

const STATUS_COLOUR: Record<GuestRequestStatus, string> = {
  pending: tokens.warning,
  accepted: tokens.brand,
  completed: tokens.success,
  declined: tokens.inkFaint,
  cancelled: tokens.inkFaint,
  refunded: tokens.error,
};

/** The three the ticket names, in the order a shift works them. */
const PRIMARY_TRANSITIONS: readonly GuestRequestTransition[] = [
  "accept",
  "complete",
  "decline",
];

const TRANSITION_ICON: Partial<
  Record<GuestRequestTransition, React.ReactNode>
> = {
  accept: <CheckCircleIcon fontSize="small" />,
  complete: <DoneAllIcon fontSize="small" />,
  decline: <BlockIcon fontSize="small" />,
};

export interface GuestRequestQueueViewProps {
  requests: GuestRequest[];
  /** The clock, passed in so the caller owns the ticking (and tests own it too). */
  now: number;
  defaultLocale: string;
  /**
   * How a room is NAMED — its number, and nothing else.
   *
   * Deliberately not "Standard (303)": this string is also the one the accept
   * dialog puts in *"This books 450 kr to room …"*, and a sentence that reads
   * "to room Standard (303)" is a sentence written by a database. The room
   * TYPE goes underneath as a caption, where it is a detail rather than the
   * identity.
   */
  roomLabel: (roomId: string) => string;
  /** The room's own name, when it has one worth showing under the number. */
  roomName?: (roomId: string) => string | null;
  /** False hides every control that would be refused by the server anyway. */
  canHandle: boolean;
  busyId?: string | null;
  loading?: boolean;
  error?: string | null;
  showSettled: boolean;
  onShowSettled: (value: boolean) => void;
  onRefresh: () => void;
  onAct: (request: GuestRequest, transition: GuestRequestTransition) => void;
  /** Names the hotel, when several queues are stacked on a chain page. */
  title?: string;
}

/** The list itself, with no fetching in it, so its rendering is testable. */
export function GuestRequestQueueView({
  requests,
  now,
  defaultLocale,
  roomLabel,
  roomName,
  canHandle,
  busyId,
  loading,
  error,
  showSettled,
  onShowSettled,
  onRefresh,
  onAct,
  title,
}: GuestRequestQueueViewProps) {
  const waiting = requests.filter((r) => r.status === "pending").length;
  const visible = requests.filter(
    (r) => showSettled || OPEN_STATUSES.includes(r.status)
  );

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        flexWrap="wrap"
        data-testid="request-queue-toolbar"
      >
        {title && (
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: tokens.ink }}>
            {title}
          </Typography>
        )}
        <Chip
          size="small"
          label={
            waiting === 0
              ? "Nothing waiting"
              : waiting === 1
                ? "1 waiting"
                : `${waiting} waiting`
          }
          data-testid="request-waiting-count"
          sx={{
            height: 22,
            fontSize: 11.5,
            fontWeight: 700,
            color: waiting ? tokens.warning : tokens.inkMuted,
            borderColor: waiting ? tokens.warning : tokens.divider,
          }}
          variant="outlined"
        />
        {loading && <CircularProgress size={14} />}
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showSettled}
              onChange={(e) => onShowSettled(e.target.checked)}
              data-testid="request-show-settled"
            />
          }
          label={
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Show settled
            </Typography>
          }
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          data-testid="request-refresh"
        >
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Room</TableCell>
              <TableCell>Request</TableCell>
              <TableCell>What it is</TableCell>
              <TableCell>Waited</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" width={canHandle ? 300 : 0} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((request) => (
              <QueueRow
                key={request.id}
                request={request}
                now={now}
                defaultLocale={defaultLocale}
                roomLabel={roomLabel}
                roomName={roomName}
                canHandle={canHandle}
                busy={busyId === request.id}
                onAct={onAct}
              />
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Stack alignItems="center" sx={{ py: 4 }} gap={0.5}>
                    <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
                      {loading ? "Loading…" : "Nothing waiting"}
                    </Typography>
                    {!loading && (
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: tokens.inkMuted,
                          textAlign: "center",
                          maxWidth: 460,
                        }}
                      >
                        When a guest confirms something on their television, it
                        lands here within a few seconds.
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

function QueueRow({
  request,
  now,
  defaultLocale,
  roomLabel,
  roomName,
  canHandle,
  busy,
  onAct,
}: {
  request: GuestRequest;
  now: number;
  defaultLocale: string;
  roomLabel: (roomId: string) => string;
  roomName?: (roomId: string) => string | null;
  canHandle: boolean;
  busy: boolean;
  onAct: (request: GuestRequest, transition: GuestRequestTransition) => void;
}) {
  const shape = requestShape(request);
  const amount = requestAmount(request);
  const picks = selectionLine(request, defaultLocale);
  const reason = latestReason(request);
  const actor = latestActor(request);
  const allowed = availableTransitions(request.status);
  const primary = PRIMARY_TRANSITIONS.filter((t) => allowed.includes(t));
  const secondary = SECONDARY_TRANSITIONS.filter((t) => allowed.includes(t));
  const waitedMs = Math.max(0, now - new Date(request.createdAt).getTime());
  // Only a request nobody has touched is "late". Once staff have accepted it
  // the clock is a fact, not an alarm — the hotel has taken it on.
  const urgent = request.status === "pending" && waitedMs >= WAIT_LATE_MS;
  const nagging = request.status === "pending" && waitedMs >= WAIT_WARN_MS;

  return (
    <TableRow hover data-testid={`request-row-${request.id}`}>
      <TableCell>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: tokens.ink }}>
          {roomLabel(request.roomId)}
        </Typography>
        {roomName?.(request.roomId) && (
          <Typography sx={{ fontSize: 11.5, color: tokens.inkFaint }}>
            {roomName(request.roomId)}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        {/* No glyph: a `GuestRequest` snapshots the offer's TITLE and the item
            labels, never an icon key, and looking one up in today's catalogue
            would draw last week's order with this week's picture. */}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, color: tokens.ink }}>
            {resolveLocalized(
              request.offerTitle,
              defaultLocale,
              defaultLocale
            ) || "Untitled offer"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: tokens.inkFaint }}>
            {shape === "order"
              ? "Order"
              : shape === "booking"
                ? "Booking"
                : "Asked for staff"}
          </Typography>
        </Box>
      </TableCell>

      <TableCell sx={{ color: tokens.inkMuted }}>
        <Stack spacing={0.25}>
          {/* An order: what and how many, then the money. */}
          {shape === "order" && (
            <>
              {picks && (
                <Typography sx={{ fontSize: 13, color: tokens.ink }}>
                  {picks}
                </Typography>
              )}
              <Typography sx={{ fontSize: 12 }}>
                {[
                  `${selectionQuantity(request)} item${
                    selectionQuantity(request) === 1 ? "" : "s"
                  }`,
                  amount,
                  PAYMENT_METHOD_LABEL[request.payment.method],
                  request.payment.method === "none"
                    ? null
                    : PAYMENT_STATUS_LABEL[request.payment.status],
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </>
          )}

          {/* A booking: the hour it took, and who it seats. */}
          {shape === "booking" && (
            <>
              <Typography sx={{ fontSize: 13, color: tokens.ink }}>
                {formatSlot(request.slotStart ?? "")}
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                {[
                  request.partySize
                    ? `Party of ${request.partySize}`
                    : "Party not stated",
                  amount,
                  request.payment.method === "none"
                    ? null
                    : PAYMENT_METHOD_LABEL[request.payment.method],
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </>
          )}

          {/* Asked for staff: the room and the clock are the whole story. The
              picked amenity is the one thing worth adding, because it is what
              somebody has to carry up. No price, because there is none. */}
          {shape === "asap" && (
            <Typography sx={{ fontSize: 13, color: tokens.ink }}>
              {picks || "Nothing picked — the guest just asked for staff."}
            </Typography>
          )}

          {request.note && (
            <Typography sx={{ fontSize: 12, fontStyle: "italic" }}>
              “{request.note}”
            </Typography>
          )}
          {reason && (
            <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
              Reason: {reason}
            </Typography>
          )}
        </Stack>
      </TableCell>

      <TableCell>
        <Typography
          data-testid={`request-waited-${request.id}`}
          sx={{
            fontSize: 13,
            fontWeight: urgent ? 700 : 500,
            color: urgent
              ? tokens.error
              : nagging
                ? tokens.warning
                : tokens.inkMuted,
          }}
        >
          {waitedLabel(request.createdAt, now)}
        </Typography>
      </TableCell>

      <TableCell>
        <Stack spacing={0.25} alignItems="flex-start">
          <Chip
            size="small"
            variant="outlined"
            label={REQUEST_STATUS_LABEL[request.status]}
            data-testid={`request-status-${request.id}`}
            sx={{
              height: 22,
              fontSize: 11.5,
              fontWeight: 700,
              color: STATUS_COLOUR[request.status],
              borderColor: STATUS_COLOUR[request.status],
            }}
          />
          {/* Every move stamps an actor (§13). Showing it is what makes the
              stamp worth taking — an accepted row whose acceptance nobody owns
              is exactly what the rule exists to prevent. */}
          {actor && request.status !== "pending" && (
            <Typography sx={{ fontSize: 11, color: tokens.inkFaint }}>
              by {actor}
            </Typography>
          )}
          {guestRequestIsRevenue(request.status) && amount && (
            <Typography sx={{ fontSize: 11, color: tokens.success }}>
              {amount} booked
            </Typography>
          )}
        </Stack>
      </TableCell>

      <TableCell align="right">
        {canHandle && (
          <Stack direction="row" gap={1} justifyContent="flex-end">
            {primary.map((t) => (
              <Button
                key={t}
                size="small"
                variant={t === "accept" ? "contained" : "outlined"}
                color={t === "decline" ? "inherit" : "primary"}
                startIcon={TRANSITION_ICON[t]}
                disabled={busy}
                onClick={() => onAct(request, t)}
                data-testid={`request-${t}-${request.id}`}
              >
                {TRANSITION_LABEL[t]}
              </Button>
            ))}
            {secondary.length > 0 && (
              <RowMenu
                requestId={request.id}
                transitions={secondary}
                disabled={busy}
                onPick={(t) => onAct(request, t)}
              />
            )}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Cancel and refund, out of the way of the three verbs a shift actually uses. */
function RowMenu({
  requestId,
  transitions,
  disabled,
  onPick,
}: {
  requestId: string;
  transitions: readonly GuestRequestTransition[];
  disabled?: boolean;
  onPick: (t: GuestRequestTransition) => void;
}) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={(e) => setEl(e.currentTarget)}
        aria-label="More actions"
        data-testid={`request-more-${requestId}`}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={el} open={Boolean(el)} onClose={() => setEl(null)}>
        {transitions.map((t) => (
          <MenuItem
            key={t}
            onClick={() => {
              setEl(null);
              onPick(t);
            }}
            data-testid={`request-${t}-${requestId}`}
          >
            {TRANSITION_LABEL[t]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/* -------------------------------------------------------------------------- */

export interface GuestRequestQueueProps {
  premiseId: string;
  /** For the room column. A queue that says `65f0…c1` helps nobody. */
  rooms?: { id: string; name?: string }[];
  defaultLocale?: string;
  /** Names the hotel when several queues share a page (the chain view). */
  title?: string;
}

/**
 * One premise's queue, wired: polls, ages, and moves rows through the machine.
 *
 * Mounted once per hotel — the chain page stacks several of these rather than
 * asking for a company-layer list, because a request belongs to a room and a
 * room belongs to one location. There is no such thing as a company's request.
 */
export default function GuestRequestQueue({
  premiseId,
  rooms,
  defaultLocale = "sv",
  title,
}: GuestRequestQueueProps) {
  // The same grandfather clause the route carries (`guestRequests.ts`):
  // nothing backfills a new permission key, so an existing operator would see
  // the queue with every button dead until somebody re-saved their role.
  // Two unconditional calls, not `a() || b()`: the second would be skipped on
  // a render where the first is true, and hooks may not run conditionally.
  const holdsQueueKey = useCan("canHandleGuestRequests");
  const holdsLayoutKey = useCan("canEditLayout");
  const canHandle = holdsQueueKey || holdsLayoutKey;
  const [requests, setRequests] = useState<GuestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettled, setShowSettled] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [pending, setPending] = useState<{
    request: GuestRequest;
    transition: GuestRequestTransition;
  } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  // A poll landing after the component went away must not set state.
  const alive = useRef(true);
  /**
   * When the last write landed, and whether one is in flight.
   *
   * A poll issued a moment BEFORE Accept resolves would otherwise land after it
   * and replace the list with the pre-transition rows — the row flips back to
   * "New" with a live Accept button, and the second press is a 409 the operator
   * cannot explain. So a fetch that started before the newest write is thrown
   * away rather than applied, and no poll is applied at all while a write is in
   * flight. The next tick, eight seconds later, is authoritative again.
   */
  const lastWriteAt = useRef(0);
  const writesInFlight = useRef(0);

  // The number IS the room, and this string also lands in "…to room 412".
  const roomLabel = useCallback((id: string) => id, []);
  // The type ("Standard", "Svit") as a caption. Useful to a porter, and never
  // the thing the room is called.
  const roomName = useCallback(
    (id: string) => rooms?.find((r) => r.id === id)?.name ?? null,
    [rooms]
  );

  const refresh = useCallback(async () => {
    const startedAt = Date.now();
    try {
      const list = await foyerApi.listGuestRequests(premiseId);
      if (!alive.current) return;
      // Stale by the time it arrived: a transition has landed since this fetch
      // was issued, and its answer is the newer truth.
      if (writesInFlight.current > 0 || lastWriteAt.current > startedAt) return;
      setRequests(list);
      setError(null);
    } catch (err) {
      if (!alive.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [premiseId]);

  useEffect(() => {
    alive.current = true;
    void refresh();
    const poll = setInterval(() => void refresh(), POLL_MS);
    // The second clock. Separate from the poll on purpose — see the docblock.
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      alive.current = false;
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [refresh]);

  const commit = async (
    transition: GuestRequestTransition,
    reason?: string
  ) => {
    if (!pending) return;
    const id = pending.request.id;
    setBusyId(id);
    setDialogError(null);
    writesInFlight.current += 1;
    let refused = false;
    try {
      const saved = await foyerApi.transitionGuestRequest(
        premiseId,
        id,
        transition,
        reason
      );
      // Adopt the server's own row rather than patching the status locally: it
      // is the row that carries the new stamp, and a locally-guessed status
      // would disagree with the audit line printed underneath it.
      lastWriteAt.current = Date.now();
      setRequests((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
      setPending(null);
    } catch (err) {
      // A lost race ("somebody else already accepted this") answers 409. Say so
      // and refetch, rather than leaving a dialog that seems to have worked.
      setDialogError(err instanceof Error ? err.message : String(err));
      refused = true;
    } finally {
      writesInFlight.current -= 1;
      setBusyId(null);
    }
    // Outside the `finally`, so the counter is already back to zero and the
    // guard in `refresh` does not throw away the very answer we are asking for.
    if (refused) void refresh();
  };

  return (
    <>
      <GuestRequestQueueView
        requests={requests}
        now={now}
        defaultLocale={defaultLocale}
        roomLabel={roomLabel}
        roomName={roomName}
        canHandle={canHandle}
        busyId={busyId}
        loading={loading}
        error={error}
        showSettled={showSettled}
        onShowSettled={setShowSettled}
        onRefresh={() => void refresh()}
        onAct={(request, transition) => {
          setDialogError(null);
          setPending({ request, transition });
        }}
        title={title}
      />
      {pending && (
        // Keyed by row AND verb: the remount is the reset, so a reason typed
        // for one decline can never follow the operator to the next row.
        <GuestRequestActionDialog
          key={`${pending.request.id}:${pending.transition}`}
          request={pending.request}
          transition={pending.transition}
          roomLabel={roomLabel(pending.request.roomId)}
          busy={busyId === pending.request.id}
          error={dialogError}
          onClose={() => setPending(null)}
          onConfirm={(reason) => void commit(pending.transition, reason)}
        />
      )}
    </>
  );
}
